import Btn from '../ui/Btn';
import { Icon } from '../ui/Icon';
import { useIsMobile } from '../../lib/useBreakpoint';

/**
 * LimitReachedModal — friendly upgrade prompt when a plan limit is hit.
 *
 * Phase 3A: parameterized to handle different limit types.
 *
 * Props:
 *   type: 'ai' | 'storage' | 'members' | 'workspaces' — determines copy
 *   used, limit: usage numbers to display
 *   plan: current plan slug (for "Upgrade to X" pitch)
 *   onClose: dismiss without upgrading
 *   onUpgrade: navigate to billing
 */
const LIMIT_COPY = {
  ai: {
    title: "You've reached your AI limit",
    unitLabel: 'AI classifications',
    cycleLabel: 'this cycle',
    proPerks: [
      'Unlimited AI classifications',
      '500 documents stored',
      'Custom branding + logo',
    ],
    businessPerks: [
      'Unlimited everything',
      'SMS notifications',
      'Priority support',
    ],
    safetyNote: 'Your existing documents are safe. Only new AI classifications are paused.',
  },
  storage: {
    title: "You've reached your storage limit",
    unitLabel: 'documents',
    cycleLabel: 'stored',
    proPerks: [
      '500 documents stored',
      'Unlimited AI classifications',
      '3 workspaces',
      'Custom branding + logo',
    ],
    businessPerks: [
      'Unlimited document storage',
      'Unlimited workspaces',
      'SMS notifications',
    ],
    safetyNote: 'Delete old documents to free up space, or upgrade for more room.',
  },
  members: {
    title: "You've reached your member limit",
    unitLabel: 'staff members',
    cycleLabel: 'in this workspace',
    proPerks: [
      'Unlimited staff members',
      'Unlimited AI classifications',
      '500 documents stored',
      'Custom branding + logo',
    ],
    businessPerks: [
      'Unlimited everything',
      'Unlimited workspaces',
      'SMS notifications',
    ],
    safetyNote: 'Client portal users never count toward this limit.',
  },
  workspaces: {
    title: "You've reached your workspace limit",
    unitLabel: 'workspaces',
    cycleLabel: 'created',
    proPerks: [
      '3 workspaces',
      'Unlimited AI classifications',
      '500 documents stored',
      'Custom branding + logo',
    ],
    businessPerks: [
      'Unlimited workspaces',
      'Unlimited everything',
      'SMS notifications',
    ],
    safetyNote: 'Upgrade to manage multiple clients or offices under one account.',
  },
};

export default function LimitReachedModal({
  type = 'ai',
  used,
  limit,
  plan = 'free',
  onClose,
  onUpgrade,
}) {
  const isMobile = useIsMobile();
  const copy = LIMIT_COPY[type] || LIMIT_COPY.ai;
  const nextPlan = plan === 'free' ? 'Pro' : 'Business';
  const nextPerks = plan === 'free' ? copy.proPerks : copy.businessPerks;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(10, 15, 46, 0.55)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
        animation: 'fadeIn 0.2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-lg)',
          maxWidth: 460, width: '100%',
          boxShadow: 'var(--shadow-xl)',
          overflow: 'hidden',
          border: '1px solid var(--border)',
        }}
      >
        {/* Hero */}
        <div style={{
          padding: isMobile ? '24px 20px 18px' : '34px 30px 22px',
          background: 'linear-gradient(135deg, var(--accent-bg) 0%, transparent 100%)',
          textAlign: 'center',
          borderBottom: '1px solid var(--border)',
        }}>
          <div style={{
            width: 58, height: 58, margin: '0 auto 16px',
            borderRadius: 16,
            background: 'var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 10px 24px rgba(61,199,91,0.28)',
          }}>
            <Icon.zap size={26} stroke="#fff" strokeWidth={2.5} />
          </div>
          <div style={{
            fontSize: 20, fontWeight: 800,
            letterSpacing: '-0.02em', marginBottom: 6,
            color: 'var(--text)',
          }}>
            {copy.title}
          </div>
          <div style={{ fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.55, maxWidth: 360, margin: '0 auto' }}>
            Your <strong style={{ color: 'var(--text)', textTransform: 'capitalize' }}>{plan}</strong> plan includes{' '}
            <strong style={{ color: 'var(--text)' }}>{limit.toLocaleString()}</strong>{' '}
            {copy.unitLabel} {copy.cycleLabel}.
            {typeof used === 'number' && (
              <> You currently have <strong style={{ color: 'var(--text)' }}>{used.toLocaleString()}</strong>.</>
            )}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: isMobile ? '18px 20px 22px' : '22px 30px 26px' }}>
          <div style={{
            padding: 16,
            background: 'var(--accent-bg)',
            border: '1px solid rgba(61,199,91,0.25)',
            borderRadius: 'var(--r-md)',
            marginBottom: 20,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              marginBottom: 12,
            }}>
              <div style={{
                width: 26, height: 26, borderRadius: 8,
                background: 'var(--accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Icon.zap size={13} stroke="#fff" strokeWidth={2.8} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.01em' }}>
                Upgrade to {nextPlan}
              </div>
            </div>
            <ul style={{
              margin: 0, padding: 0, listStyle: 'none',
              display: 'flex', flexDirection: 'column', gap: 7,
            }}>
              {nextPerks.map((perk, i) => (
                <li key={i} style={{
                  fontSize: 12.5, color: 'var(--text-2)',
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  lineHeight: 1.5,
                }}>
                  <div style={{
                    width: 14, height: 14, borderRadius: '50%',
                    background: 'rgba(61,199,91,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, marginTop: 2,
                  }}>
                    <Icon.check size={8} stroke="var(--accent-600)" strokeWidth={3.5} />
                  </div>
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="outline" size="md" onClick={onClose} style={{ flex: 1 }}>
              Not now
            </Btn>
            <Btn
              variant="primary" size="md"
              iconRight={Icon.arrowR}
              onClick={onUpgrade}
              style={{ flex: 2 }}
            >
              See plans
            </Btn>
          </div>

          <div style={{
            fontSize: 11, color: 'var(--text-muted)',
            textAlign: 'center', marginTop: 14, lineHeight: 1.5,
          }}>
            {copy.safetyNote}
          </div>
        </div>
      </div>
    </div>
  );
}
